import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { 
  updateFormData, 
  setWalletBalance,
  setTransactionReceipt
} from '../redux/actions/investmentForm';
import { 
  selectFormData, 
  selectFunderId, 
  selectCurrency
} from '../redux/selectors/investmentFormSelectors';
import { convertToBase64 } from "../helpers/convertToBase64";
import useInvestmentWalletBalance from './useInvestmentWalletBalance';

export const useInvestmentAmountDetails = (form: any, productId?: number) => {
  const dispatch = useDispatch();
  
  // Get data from Redux
  const formData = useSelector(selectFormData);
  const funderId = useSelector(selectFunderId);
  const currencyCode = useSelector(selectCurrency) || 'NGN';
  
  // We now get the wallet balance directly from the selected wallet
  // so we don't need to use the wallet balance hook anymore
  
  const [fileList, setFileList] = useState<any[]>([]);
  
  // Initialize form with values from redux when component mounts
  useEffect(() => {
    if (formData) {
      form.setFieldsValue({
        principal_amount: formData.principal_amount,
        amount_to_credit: formData.amount_to_credit,
        effective_date: formData.effective_date ? moment(formData.effective_date) : undefined
      });
    }
  }, [form, formData]);
  
  // We no longer need to store wallet balance from the hook since we get it directly from the selected wallet
  
  // We now calculate required top-up amount in the component based on the selected wallet
  
  // Synchronize fileList with the form value when component mounts or form changes
  useEffect(() => {
    const currentFile = form.getFieldValue('transaction_receipt');
    if (currentFile && fileList.length === 0) {
      setFileList([{
        uid: '-1',
        name: currentFile.name || 'Uploaded file',
        status: 'done',
        originFileObj: currentFile
      }]);
    }
  }, [form, fileList.length]);
  
  // Handle principal amount change
  const handlePrincipalAmountChange = (value: number | null) => {
    const numValue = Number(value || 0);
    
    // Update local form
    form.setFieldsValue({ principal_amount: numValue });
    
    // Update Redux state
    dispatch(updateFormData({ principal_amount: numValue }));
    
    // We removed the wallet balance validation here since we now
    // perform this validation in the component using the selected wallet's balance
    // This happens directly in the InvestmentModalAmountDetails component
    // based on the selected wallet
  };
  
  // Handle top-up amount change
  const handleTopUpAmountChange = (value: number | null) => {
    const numValue = Number(value || 0);
    
    // Update local form
    form.setFieldsValue({ amount_to_credit: numValue });
    
    // Update Redux state
    dispatch(updateFormData({ amount_to_credit: numValue }));
  };
  
  // Handle effective date change
  const handleEffectiveDateChange = (date: moment.Moment | null) => {
    if (date) {
      const formattedDate = date.format('YYYY-MM-DD');
      
      // Update Redux state
      dispatch(updateFormData({ effective_date: formattedDate }));
    }
  };

  // Handle file upload
  const handleFileUpload = async (file: File) => {
    try {
      const fileBase64 = await convertToBase64(file);
      
      // Update Redux state
      dispatch(setTransactionReceipt({
        base64: fileBase64 as string,
        fileName: file.name,
      }));
      
      // Update form field
      form.setFieldsValue({
        transaction_receipt: file
      });
      
      // Update file list for UI
      setFileList([{
        uid: '-1',
        name: file.name,
        status: 'done',
        originFileObj: file
      }]);
      
      return false; // Prevent default upload behavior
    } catch (error) {
      console.error("Error converting file to base64:", error);
      return false;
    }
  };

  // Handle file removal
  const handleFileRemove = () => {
    // Clear form field value
    form.setFieldsValue({
      transaction_receipt: undefined
    });
    
    // Clear Redux state
    dispatch(setTransactionReceipt({
      base64: "",
      fileName: "",
    }));
    
    // Clear fileList
    setFileList([]);
    
    // Validate field to show error
    form.validateFields(['transaction_receipt']);
  };

  // We now handle validation in the component based on the selected wallet's balance
  // This code is no longer needed here

  return {
    currencyCode,
    fileList,
    handlePrincipalAmountChange,
    handleTopUpAmountChange,
    handleEffectiveDateChange,
    handleFileUpload,
    handleFileRemove
  };
};